<script>
import Popper from 'popper.js';

export default {
  name: 'EditorGuide',
  props: {
    element: {
      validator: el => {
        return el.tagName;
      }
    },
    hasError: Boolean,
    hidden: {
      type: Boolean,
      default: false
    },
    icon: String,
    text: String,
    title: String
  },
  data() {
    return {
      hasFocus: false,
      hasHover: false,
      popper: null
    };
  },
  watch: {
    hidden() {
      this.popper.scheduleUpdate();
    }
  },
  mounted() {
    this.popper = new Popper(this.element, this.$refs.guide, {
      placement: 'top-start',
      removeOnDestroy: true,
      modifiers: {
        flip: {
          enabled: false
        },
        offset: {
          enabled: true,
          offset: '0px, 8px'
        },
        preventOverflow: {
          enabled: false
        },
        hide: {
          enabled: false
        }
      }
    });

    this.element.addEventListener('focus', this.handleFocus);
    this.element.addEventListener('blur', this.handleBlur);
    this.element.addEventListener('mouseover', this.handleMouseOver);
    this.element.addEventListener('mouseout', this.handleMouseOut);
  },
  beforeDestroy() {
    // TODO - why is this creating an error? It only happens after TinyMCE is initialized on the
    // same element.
    //
    // this.popper.destroy();

    this.element.removeEventListener('focus', this.handleFocus);
    this.element.removeEventListener('blur', this.handleBlur);
    this.element.removeEventListener('mouseover', this.handleMouseOver);
    this.element.removeEventListener('mouseout', this.handleMouseOut);
  },
  methods: {
    handleClick() {
      this.$emit('click', this.element);
    },

    handleFocus() {
      this.hasFocus = true;
    },

    handleBlur() {
      this.hasFocus = false;
    },

    handleMouseOver() {
      this.hasHover = true;
    },

    handleMouseOut() {
      this.hasHover = false;
    }
  }
};
</script>

<template>
  <div
    ref="guide"
    :class="{
      'editor-guide': true,
      'has-error': hasError,
      'has-focus': hasFocus,
      'has-hover': hasHover,
      'is-hidden': hidden || hasFocus
    }"
    :title="title"
    @click="handleClick"
  >
    <div class="editor-guide-body">
      <i v-if="icon" :class="icon" />
      <span v-if="text" class="text">{{ text }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

$--editor-guide-bg: $--color-gray-90;
$--editor-guide-color: $--color-white;
$--editor-guide-bg-hover: $--color-primary;
$--editor-guide-color-hover: $--color-white;
$--editor-guide-bg-focus: $--color-primary;
$--editor-guide-color-focus: $--color-white;
$--editor-guide-hover-distance: -0.3rem;
$--editor-guide-error-bg: $--color-danger;
$--editor-guide-error-color: $--color-white;

.editor-guide {
  transition: 0.2s padding ease-in-out, 0.2s opacity ease-in-out;

  .editor-guide-body {
    position: relative;
    background-color: $--editor-guide-bg;
    color: $--editor-guide-color;
    padding: 0.5rem;
    border-radius: 0.25rem;
    box-shadow: 0 1px 0.5rem rgba($--color-black, 0.125);
    cursor: pointer;
    transition: 0.2s background-color, 0.2s margin ease-in-out;

    // Tip
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      height: 0;
      width: 0;
      border: solid transparent;
      border-color: transparent;
      border-top-color: $--editor-guide-bg;
      border-width: 6px;
      margin-left: -6px;
      transition: 0.2s border;
    }

    i + .text {
      margin-left: 0.25rem;
    }
  }

  // Hover state
  &:hover,
  &.has-hover {
    padding-bottom: abs($--editor-guide-hover-distance);

    .editor-guide-body {
      background-color: $--editor-guide-bg-hover;
      color: $--editor-guide-color-hover;
      margin-top: $--editor-guide-hover-distance;

      &::after {
        border-top-color: $--editor-guide-bg-hover;
        color: $--editor-guide-color-hover;
      }
    }
  }

  // Focus state
  &.has-focus {
    .editor-guide-body {
      background-color: $--editor-guide-bg-focus;
      color: $--editor-guide-color-focus;

      &::after {
        border-top-color: $--editor-guide-bg-focus;
        color: $--editor-guide-color;
      }
    }
  }

  // Error state
  &.has-error {
    .editor-guide-body {
      background-color: $--editor-guide-error-bg;
      color: $--editor-guide-error-color;
      cursor: default;

      &::after {
        border-top-color: $--editor-guide-error-bg;
        color: $--editor-guide-error-color;
      }
    }
  }

  // Hidden
  &.is-hidden {
    opacity: 0;
    pointer-events: none;
  }
}
</style>
